import { type Overwrite } from 'utility-types';
import { z } from 'zod';

import * as env from './deployment-env';
import * as util from './util';

/**
 * A schema that represents the shape of the client environment variables that are exposed via the
 * /api/client-environment endpoint and injected into public pages' server-side props.  This is
 * the minimal environment any page can require: the values (application version, Datadog
 * credentials, and the deployment environment) that observability bootstrapping needs on every
 * document.  Environment values that only non-public pages require live on the
 * {@link UnsafeProtectedPageEnvironmentSchema} extension instead and are never exposed through
 * the endpoint.
 *
 * The Datadog credentials belong to this base schema, rather than the protected page-level
 * extension, because Datadog must be able to initialize on every document the application serves,
 * not only on pages whose server-side props inject an environment.  The NextJS error pages (`/404`
 * and `/500`) are deliberately kept statically generated and cannot run `getServerSideProps`, so
 * the only way for them to obtain the Datadog credentials is the /api/client-environment fallback
 * request, and that endpoint serves exactly this schema.  The credentials are also inherently
 * browser-public (they ship to every client by design), so exposing them through an
 * unauthenticated endpoint sacrifices nothing.
 *
 * The `deploymentEnv` value uses the full {@link env.EnvironmentSchema} enum because the deployed
 * test environment runs with `DEPLOYMENT_ENV=test`; the `test` value is a real deployed environment
 * value, not exclusively a Jest-only value, so the schema must accept it.
 *
 * This schema is considered "unsafe" to parse with directly, and the method
 * {@link safelyParseClientRuntimeEnvironment} should be used instead to ensure that the
 * TypeScript type system can enforce the presence of required variables and the absence of
 * unnecessary variables when parsing the client environment.
 */
export const UnsafeClientRuntimeEnvironmentSchema = z.object({
  appVersion: util.SerializableOptionalStringVariableSchema,
  datadog: z.object({
    appId: util.SerializableOptionalStringVariableSchema,
    clientToken: util.SerializableOptionalStringVariableSchema,
  }),
  deploymentEnv: env.EnvironmentSchema,
});

/**
 * Extends {@link UnsafeClientRuntimeEnvironmentSchema} with the environment values that only
 * non-public (Auth0-authenticated and LTI) pages require.
 *
 * These values are never exposed via the /api/client-environment endpoint.  They reach the client
 * exclusively through the server-side props injected by the non-public page-context wrappers,
 * which guarantee their presence by failing the page request (rendering the 500 page) when
 * parsing fails.
 */
export const UnsafeProtectedPageEnvironmentSchema = UnsafeClientRuntimeEnvironmentSchema.extend({
  pactSafe: z.object({
    accessId: util.RequiredStringVariableSchema,
    groupKey: util.RequiredStringVariableSchema,
  }),
  statsig: z.object({
    clientKey: util.RequiredStringVariableSchema,
  }),
  userflowToken: util.SerializableOptionalStringVariableSchema,
});

/**
 * The environment injected into a public page's server-side props by `publicPageContext`, and the
 * shape served by the /api/client-environment endpoint.
 */
export type PublicPageEnv = z.infer<typeof UnsafeClientRuntimeEnvironmentSchema>;

/**
 * The environment injected into a non-public (Auth0 or LTI) page's server-side props by
 * `authenticatedPageContext` and `ltiPageContext`: a superset of {@link PublicPageEnv} carrying
 * the values only authenticated experiences require.
 */
export type ProtectedPageEnv = z.infer<typeof UnsafeProtectedPageEnvironmentSchema>;

type _InferredClientRuntimeEnvironment = z.input<typeof UnsafeClientRuntimeEnvironmentSchema>;

interface _DeepNullableArray<T> extends Array<_DeepNullable<T>> {}

type _DeepNullable<T> = T extends () => unknown
  ? T
  : T extends (infer U)[]
    ? _DeepNullableArray<U>
    : T extends object
      ? DeepNullable<T>
      : null | T;

/**
 * A generic type that allows all properties or elements of a given type {@link T} to be nullable.
 *
 * @example
 * ```ts
 * DeepNullable<{ a: string; b: { c: number[] } }> would be equivalent to:
 * { a: string | null; b: { c: (number | null)[] | null } | null }
 * ```
 */
type DeepNullable<T> = {
  [P in keyof T]: _DeepNullable<T[P]>;
};

/**
 * The input type for the {@link safelyParseClientRuntimeEnvironment} method which ensures that all
 * client environment variables are explicitly defined and not omitted, with 'null' values used
 * to represent optional variables.  This ensures that when parsing the client environment,
 * certain environment variables are not unintentionally excluded.
 */
type SafelyParseClientRuntimeEnvironmentInput = DeepNullable<
  Overwrite<
    _InferredClientRuntimeEnvironment,
    {
      readonly deploymentEnv: string | undefined;
    }
  >
>;

type SafelyParsedClientRuntimeEnvironment = z.SafeParseReturnType<
  z.infer<typeof UnsafeClientRuntimeEnvironmentSchema>,
  z.infer<typeof UnsafeClientRuntimeEnvironmentSchema>
>;

/**
 * Safely parses the client environment variables from an input whose shape conforms to the
 * expected shape of the client environment variables, {@link ClientRuntimeEnvironment}, but where
 * the variables may be missing or malformed.
 *
 * The purpose of this method is to ensure that the TypeScript will not compile the code when
 * parsing environment variables with {@link UnsafeClientRuntimeEnvironmentSchema} if the variables
 * that are provided would cause the schema parsing to fail in runtime due to missing and/or
 * unnecessarily provided variables, ensuring consistency with the input to this function and
 * the shape of the {@link UnsafeClientRuntimeEnvironmentSchema}.
 *
 * @param {SafelyParseClientRuntimeEnvironmentInput} input
 *   The input that should be parsed by the {@link UnsafeClientRuntimeEnvironmentSchema}. The shape
 *   of this input is the same as the shape of the {@link UnsafeClientRuntimeEnvironmentSchema},
 *   with certain mild modifications to ensure that the TypeScript type system can enforce the
 *   presence of required  variables and the absence of unnecessary variables.
 *
 * @example
 * ```ts
 * // Providing unnecessary/additional variables will result in a compilation error rather than
 * // a runtime error.
 * const result = safelyParseClientRuntimeEnvironment({
 *   appVersion: process.env.APP_VERSION,
 *   // 'sentry' is not defined in the schema.
 *   sentry: process.env.SENTRY_DSN,
 * });
 * ```
 *
 * @returns {SafelyParsedClientRuntimeEnvironment}
 *   The parsed client environment variables.  The shape of this output is the same as the shape of
 *   the {@link UnsafeClientRuntimeEnvironmentSchema}.
 */
export const safelyParseClientRuntimeEnvironment = (
  input: SafelyParseClientRuntimeEnvironmentInput,
): SafelyParsedClientRuntimeEnvironment =>
  UnsafeClientRuntimeEnvironmentSchema.strict().safeParse(input);

/**
 * Reads the auth-agnostic slice of the client environment from the server process environment:
 * the values every page requires regardless of its page-auth type.
 *
 * This mapping is the single source of truth for which process environment variables feed the
 * base client runtime environment. Both from-process parsers,
 * {@link safelyParseClientRuntimeEnvironmentFromProcess} and
 * {@link safelyParseProtectedPageEnvironmentFromProcess}, spread this object so the delivery
 * paths (the /api/client-environment endpoint and the page-context wrappers) can never drift on
 * the shared values.
 *
 * @returns {SafelyParseClientRuntimeEnvironmentInput} The unparsed auth-agnostic environment.
 */
const readAuthAgnosticEnvironment = (): SafelyParseClientRuntimeEnvironmentInput =>
  ({
    appVersion: process.env.APP_VERSION,
    datadog: {
      appId: process.env.DATADOG_APPLICATION_ID,
      clientToken: process.env.DATADOG_CLIENT_TOKEN,
    },
    deploymentEnv: process.env.DEPLOYMENT_ENV,
  }) as const;

/**
 * Safely parses the base client runtime environment directly from the server process environment
 * (via {@link readAuthAgnosticEnvironment}), producing the shape that the
 * /api/client-environment endpoint serves and that `publicPageContext` injects into public pages'
 * server-side props.
 *
 * The non-public page-context wrappers parse through
 * {@link safelyParseProtectedPageEnvironmentFromProcess} instead, which extends this same
 * auth-agnostic mapping with the protected-only values.
 *
 * @returns {SafelyParsedClientRuntimeEnvironment} The parsed client environment variables.
 */
export const safelyParseClientRuntimeEnvironmentFromProcess =
  (): SafelyParsedClientRuntimeEnvironment =>
    safelyParseClientRuntimeEnvironment(readAuthAgnosticEnvironment());

/**
 * The input type for {@link safelyParseProtectedPageEnvironment}, mirroring
 * {@link SafelyParseClientRuntimeEnvironmentInput} for the protected-page extension of the
 * schema.
 */
type SafelyParseProtectedPageEnvironmentInput = DeepNullable<
  Overwrite<
    z.input<typeof UnsafeProtectedPageEnvironmentSchema>,
    { readonly deploymentEnv: string | undefined }
  >
>;

type SafelyParsedProtectedPageEnvironment = z.SafeParseReturnType<
  z.infer<typeof UnsafeProtectedPageEnvironmentSchema>,
  z.infer<typeof UnsafeProtectedPageEnvironmentSchema>
>;

/**
 * Safely parses the protected-page environment, mirroring
 * {@link safelyParseClientRuntimeEnvironment} for the {@link UnsafeProtectedPageEnvironmentSchema}
 * extension consumed by the non-public page-context wrappers.
 *
 * @param {SafelyParseProtectedPageEnvironmentInput} input
 *   The input that should be parsed by the {@link UnsafeProtectedPageEnvironmentSchema}.
 *
 * @returns {SafelyParsedProtectedPageEnvironment} The parsed protected-page environment variables.
 */
export const safelyParseProtectedPageEnvironment = (
  input: SafelyParseProtectedPageEnvironmentInput,
): SafelyParsedProtectedPageEnvironment =>
  UnsafeProtectedPageEnvironmentSchema.strict().safeParse(input);

/**
 * Safely parses the protected-page environment directly from the server process environment,
 * mirroring {@link safelyParseClientRuntimeEnvironmentFromProcess} with the additional variables
 * that only non-public pages require.
 *
 * @returns {SafelyParsedProtectedPageEnvironment} The parsed protected-page environment variables.
 */
export const safelyParseProtectedPageEnvironmentFromProcess =
  (): SafelyParsedProtectedPageEnvironment =>
    safelyParseProtectedPageEnvironment({
      ...readAuthAgnosticEnvironment(),
      pactSafe: {
        accessId: process.env.PACT_SAFE_ACCESS_ID ?? null,
        groupKey: process.env.PACT_SAFE_GROUP_KEY ?? null,
      },
      statsig: {
        clientKey: process.env.STATSIG_CLIENT_SDK_KEY ?? null,
      },
      userflowToken: process.env.USERFLOW_TOKEN,
    });

export type ReadyClientRuntimeEnvironment = {
  readonly env: z.infer<typeof UnsafeClientRuntimeEnvironmentSchema>;
  readonly isReady: true;
};

export type NotReadyClientRuntimeEnvironment = {
  readonly env: Record<never, never>;
  readonly isReady: false;
};

/**
 * Represents the client runtime environment as consumers observe it through the
 * `EnvironmentProvider` context: the base environment variables (whether server-injected through
 * a page-context wrapper or fetched from the /api/client-environment endpoint), along with a
 * boolean that indicates whether they have been loaded and are ready to be accessed.
 */
export type ClientRuntimeEnvironment =
  | NotReadyClientRuntimeEnvironment
  | ReadyClientRuntimeEnvironment;

export const DefaultClientRuntimeEnvironment: NotReadyClientRuntimeEnvironment = {
  env: {},
  isReady: false,
};

/**
 * A complete set of mocked client environment variables that the {@link MockEnvironmentProvider}
 * uses as the default environment in tests, before applying any test-specific overrides that are
 * supplied via its 'env' prop.
 */
export const MockedClientRuntimeEnvironmentVariables: ReadyClientRuntimeEnvironment['env'] = {
  appVersion: '0.0.0',
  datadog: { appId: null, clientToken: null },
  deploymentEnv: env.Environment.Test,
};

/**
 * A complete set of mocked protected-page environment variables, extending
 * {@link MockedClientRuntimeEnvironmentVariables} with the values that only non-public pages
 * receive through their page-context wrappers. Tests that render authenticated or LTI trees use
 * this as the injected page environment.
 */
export const MockedProtectedPageEnvironmentVariables: ProtectedPageEnv = {
  ...MockedClientRuntimeEnvironmentVariables,
  pactSafe: { accessId: 'mocked-pactsafe-access-id', groupKey: 'mocked-pactsafe-group-key' },
  statsig: { clientKey: 'client-mockedStatsigClientKey' },
  userflowToken: null,
};
